import { useState, useEffect, useMemo } from 'react';
import {
  IconTrendingUp,
  IconUsers,
  IconBuilding,
  IconClipboardList,
  IconTag,
  IconBox,
  IconUserPlus,
  IconBriefcase,
  IconWallet,
  IconFileInvoice,
  IconShieldLock,
  IconChartBar,
  IconPackages,
} from '@tabler/icons-react';
import { Stack, Tooltip, UnstyledButton } from '@mantine/core';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classes from './Navbar.module.css';
import { useAuth } from '../contexts/AuthProvider';
import { PAGE_KEYS, PageKey, effectiveAccessForPage } from '../utility/pageAccess';

interface NavbarLinkProps {
  icon: typeof IconTrendingUp;
  label: string;
  active?: boolean;
  onClick?: () => void;
  collapsed?: boolean;
  route?: string;
}

function NavbarLink({
  icon: Icon,
  label,
  active,
  onClick,
  collapsed = true,
  route,
}: NavbarLinkProps) {
  const content = (
    <div
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: collapsed ? 'center' : 'flex-start',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <Icon size={20} stroke={1.5} />
      {!collapsed && <span>{label}</span>}
    </div>
  );

  const buttonContent = collapsed ? (
    <Tooltip label={label} position="right" transitionProps={{ transition: 'fade', duration: 0 }}>
      {content}
    </Tooltip>
  ) : (
    content
  );

  if (route) {
    return (
      <UnstyledButton
        component={Link}
        to={route}
        onClick={onClick}
        className={classes.link}
        data-active={active || undefined}
      >
        {buttonContent}
      </UnstyledButton>
    );
  }

  return (
    <UnstyledButton
      onClick={onClick}
      className={classes.link}
      data-active={active || undefined}
    >
      {buttonContent}
    </UnstyledButton>
  );
}

interface NavLinkDef {
  icon: typeof IconTrendingUp;
  labelKey: string;
  route?: string;
  requiredRoles: string[];
  /** CRM module key for hiding sidebar entries */
  pageKey?: PageKey;
}

const mockdata: NavLinkDef[] = [
  { icon: IconTrendingUp, labelKey: 'nav.opportunity', route: '/', requiredRoles: ['admin', 'user'], pageKey: PAGE_KEYS.opportunity },
  { icon: IconChartBar, labelKey: 'nav.kpis', route: '/kpis', requiredRoles: ['admin', 'user'], pageKey: PAGE_KEYS.kpis },
  { icon: IconTag , labelKey: 'nav.campaigns', route: '/campaigns' , requiredRoles: ['admin', 'user'], pageKey: PAGE_KEYS.campaigns },
  { icon: IconBuilding, labelKey: 'nav.clients', route: '/clients' , requiredRoles: ['admin', 'user'], pageKey: PAGE_KEYS.clients },
  { icon: IconUsers, labelKey: 'nav.contacts', route: '/contacts' , requiredRoles: ['admin', 'user'], pageKey: PAGE_KEYS.contacts },
  { icon: IconClipboardList, labelKey: 'nav.tasks', route: '/tasks', requiredRoles: ['admin', 'user'], pageKey: PAGE_KEYS.tasks },
  { icon: IconBox, labelKey: 'nav.products', route: '/products', requiredRoles: ['admin', 'user'], pageKey: PAGE_KEYS.products },
  { icon: IconPackages, labelKey: 'nav.inventory', route: '/inventory', requiredRoles: ['admin', 'user'], pageKey: PAGE_KEYS.inventory },
  { icon: IconWallet, labelKey: 'nav.balance', route: '/balance', requiredRoles: ['admin', 'user'], pageKey: PAGE_KEYS.balance },
  { icon: IconFileInvoice, labelKey: 'nav.invoices', route: '/invoices', requiredRoles: ['admin', 'user'], pageKey: PAGE_KEYS.invoices },
  { icon: IconUserPlus, labelKey: 'nav.users', route: '/users', requiredRoles: ['admin'] },
  { icon: IconBriefcase, labelKey: 'nav.enterprises', route: '/enterprises', requiredRoles: ['admin'] },
  { icon: IconShieldLock, labelKey: 'nav.pageAccess', route: '/admin/access', requiredRoles: ['admin'] },
];

interface NavbarMinimalColoredProps {
  collapsed?: boolean;
  onNavigate?: () => void;
}

export function NavbarMinimalColored({ collapsed = false, onNavigate }: NavbarMinimalColoredProps) {
  const location = useLocation();
  const [active, setActive] = useState<number | null>(null);
  const { user, pageAccess, actualEnterprise } = useAuth();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const routeIndex = mockdata.findIndex(link => link.route === location.pathname);
    setActive(routeIndex !== -1 ? routeIndex : null);
  }, [location.pathname]);

  const links = useMemo(() => mockdata.map((link, index) => {
    const isActive = link.route ? link.route === location.pathname : index === active;
    if (link.requiredRoles && !link.requiredRoles.includes(user?.role as string)) {
      return null;
    }
    if (
      link.pageKey &&
      user?.role !== 'admin' &&
      effectiveAccessForPage(pageAccess, actualEnterprise?.id, link.pageKey) === 'hidden'
    ) {
      return null;
    }
    const label = t(link.labelKey);
    return (
      <NavbarLink
        icon={link.icon}
        label={label}
        key={link.labelKey}
        active={isActive}
        onClick={() => {
          if (!link.route) setActive(index);
          onNavigate?.();
        }}
        collapsed={collapsed}
        route={link.route}
      />
    );
  }), [
    active,
    actualEnterprise?.id,
    collapsed,
    i18n.language,
    location.pathname,
    pageAccess,
    t,
    user?.role,
    onNavigate,
  ]);

  const navbarWidth = collapsed ? '70px' : '200px';
  const navbarStyle = {
    width: navbarWidth,
    transition: 'width 0.3s ease',
  };

  return (
    <nav
      className={classes.navbar}
      style={{ ...navbarStyle, height: 'calc(100vh - 70px)' }}
    >
      <div className={classes.navbarMain}>
        <Stack justify="center" spacing={0}>
          {links}
        </Stack>
      </div>
    </nav>
  );
}
